import { toBoolean, toNumber } from "utils"
import { useHistory, useLocation } from "react-router-dom"

type MapFn = (x: URLSearchParams) => URLSearchParams

export type LocationSearch = {
  params: SearchParams
  search?: string
  set: (name: string, value: any) => SearchParams
  reset: () => void
  append: (name: string, value: any) => SearchParams
  getNumber: (name: string, backup?: number | undefined) => number
  getBool: (name: string, backup?: boolean | undefined) => boolean
  get: (name: string, backup?: string | undefined) => string | undefined
  contains: (name: string, value: string) => boolean
  map: (fn: MapFn) => SearchParams
  count: (name: string) => number
  countAll: (names: string[]) => number
}

export class SearchParams extends URLSearchParams {
  map = (fn: MapFn) => {
    return fn(this) as SearchParams
  }

  getNumber = (name: string, backup?: number): number => {
    const x = this.get(name)
    return toNumber(x, backup ?? 0)
  }

  getBool = (name: string, backup?: boolean): boolean => {
    const x = this.get(name)
    return toBoolean(x, backup ?? false)
  }

  getOr = (name: string, backup?: string): string | undefined => {
    return this.get(name) || backup
  }

  appendOr = (name: string, value: any) => {
    const v = String(value).trim()
    if (!v) return this
    const all = this.getAll(name)
    const set = new Set(all)
    if (set.has(v)) {
      set.delete(v)
    } else {
      set.add(v)
    }
    // remove all
    this.delete(name)
    // and add them back
    set.forEach((v) => {
      this.append(name, v)
    })

    return this
  }

  setOr = (name: string, value: any) => {
    if (!!value) {
      this.set(name, value)
    } else {
      this.delete(name)
    }
    return this
  }

  setAll = (vs: { [key: string]: any }) => {}

  contains = (name: string, value: string): boolean => {
    return new Set(this.getAll(name)).has(value)
  }

  count = (name: string): number => {
    return this.getAll(name).length
  }

  countAll = (names: string[]): number => {
    return names.reduce((total, name) => total + this.count(name), 0)
  }
}

export function useLocationSearch(): LocationSearch {
  const h = useHistory()
  const search = useLocation().search
  const qs = new SearchParams(search)
  const set = (name: string, value: any) => {
    const s = qs.setOr(name, value)
    h.push({ search: s.toString() })
    return s
  }
  const append = (name: string, value: any) => {
    const s = qs.appendOr(name, value)
    h.push({ search: s.toString() })
    return s
  }

  const reset = () => {
    h.push({ search: "" })
  }

  return {
    params: qs,
    search,
    set,
    reset,
    append,
    //
    getNumber: qs.getNumber,
    getBool: qs.getBool,
    get: qs.getOr,
    contains: qs.contains,
    map: qs.map,
    count: qs.count,
    countAll: qs.countAll,
  }
}
